import React, { useEffect, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { useHistory } from 'react-router-dom'
import { t } from 'i18next'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import MiniLoading from 'Components/MiniLoading'
import CustomSnackbar from 'Components/CustomSnackbar'
import { isValidPhoneNumberValue } from 'Components/PhoneNumberField'
import { ROUTES } from 'Utils/constants'
import { getQuickListSettingAction } from 'redux/actions/quickListSettingsActions'
import {
  getCatalogPriceListsAction,
  getQuickListCatalogAction,
  saveQuickListCatalogAction,
} from 'redux/actions/quickListCatalogActions'
import CatalogGeneralStep from './components/CatalogGeneralStep'
import CatalogSectionsStep from './components/CatalogSectionsStep'
import CatalogProductsStep from './components/CatalogProductsStep'
import useLatestRef from './hooks/useLatestRef'
import {
  CATALOG_STEPS,
  buildCatalogPayload,
  getTodayDateString,
  isValidSpecialPrice,
  mapCatalogFormValues,
  mergeBulkUpdatedProducts,
  validateCatalogSections,
} from './utils/quickListCatalog.utils'
import './QuickListCatalog.css'

const GENERAL_FIELDS = [
  'name',
  'priceListId',
  'endDate',
  'contactPhone',
  'contactEmail',
]
const EMPTY_PRICE_LISTS = {
  base: [],
  regular: [],
  catalog: [],
  discountMatrix: [],
}

const isActiveStatus = value =>
  value === true ||
  value === 1 ||
  value === '1' ||
  value?.toString?.().toLowerCase() === 'true'

function QuickListCatalog({ id }) {
  const dispatch = useDispatch()
  const history = useHistory()
  const companyId = useSelector(state => state.company.CompanyId)

  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [step, setStep] = useState(0)
  const [quickList, setQuickList] = useState(null)
  const [catalog, setCatalog] = useState(null)
  const [hasPendingCatalogSync, setHasPendingCatalogSync] = useState(false)
  // El catálogo guardado no se pudo leer: no se permite guardar para no sobrescribirlo.
  const [hasCatalogLoadError, setHasCatalogLoadError] = useState(false)
  const [priceLists, setPriceLists] = useState(EMPTY_PRICE_LISTS)
  const [snackbarMessage, setSnackbarMessage] = useState({
    open: false,
    message: '',
    severity: 'warning',
  })

  const isQuickListActive = isActiveStatus(quickList?.Status)
  const required = t('QUICK_LIST_CATALOG.VALIDATION.REQUIRED')

  const validationSchema = yup.object({
    name: yup.string().trim().max(150).required(required),
    priceListId: yup.string().required(required),
    endDate: yup
      .string()
      .required(required)
      .test(
        'not-past',
        t('QUICK_LIST_CATALOG.VALIDATION.END_DATE_PAST'),
        value => !value || value >= getTodayDateString()
      ),
    // contactPhone es el valor de PhoneNumberField: { Prefix, countryCode, number }.
    contactPhone: yup
      .mixed()
      .test('required', required, phone => Boolean(phone?.number))
      .test(
        'valid-phone',
        t('QUICK_LIST_CATALOG.VALIDATION.INVALID_PHONE'),
        phone => !phone?.number || isValidPhoneNumberValue(phone)
      ),
    contactEmail: yup
      .string()
      .trim()
      .max(150)
      .email(t('QUICK_LIST_CATALOG.VALIDATION.INVALID_EMAIL'))
      .required(required),
    sections: yup.array().test('valid-sections', '', function (sections) {
      const errorKey = validateCatalogSections({
        sections,
        products: this.parent.products,
      })
      return errorKey
        ? this.createError({
            message: t(`QUICK_LIST_CATALOG.VALIDATION.${errorKey}`),
          })
        : true
    }),
    products: yup
      .array()
      .test(
        'valid-special-prices',
        t('QUICK_LIST_CATALOG.VALIDATION.INVALID_SPECIAL_PRICE'),
        products =>
          (products || []).every(product =>
            isValidSpecialPrice(product.specialPrice)
          )
      ),
  })

  const showMessage = (message, severity) =>
    setSnackbarMessage({ open: true, message, severity })

  const formik = useFormik({
    initialValues: mapCatalogFormValues({ quickList: null, catalog: null }),
    validationSchema,
    onSubmit: async values => {
      setIsSubmitting(true)

      try {
        const payload = buildCatalogPayload({
          values,
          productCustomListId: id,
          companyId: catalog?.CompanyId || companyId,
        })
        const result = await dispatch(saveQuickListCatalogAction(payload))

        if (result.success) {
          let savedCatalog = result.catalog

          if (!savedCatalog) {
            try {
              savedCatalog = await dispatch(getQuickListCatalogAction(id))
            } catch (reloadError) {
              // The save already succeeded. Preserve the form if the refresh fails.
            }
          }

          if (savedCatalog) {
            setCatalog(savedCatalog)
            await formik.setValues(
              mapCatalogFormValues({ quickList, catalog: savedCatalog })
            )
            setHasPendingCatalogSync(false)
          } else {
            setHasPendingCatalogSync(true)
          }
          showMessage(t('QUICK_LIST_CATALOG.MESSAGE.SUCCESS_SAVE'), 'success')
        } else {
          showMessage(
            result.message || t('QUICK_LIST_CATALOG.MESSAGE.FAIL_SAVE'),
            'error'
          )
        }
      } catch (error) {
        showMessage(t('QUICK_LIST_CATALOG.MESSAGE.FAIL_SAVE'), 'error')
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const latestValuesRef = useLatestRef(formik.values)

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)

      try {
        // Un error al leer el catálogo no debe impedir cargar la lista ni las listas de precios.
        const catalogRequest = dispatch(getQuickListCatalogAction(id)).then(
          catalogItem => ({ catalogItem, failed: false }),
          () => ({ catalogItem: null, failed: true })
        )
        const [listItem, catalogResult, lists] = await Promise.all([
          dispatch(getQuickListSettingAction(id)),
          catalogRequest,
          dispatch(getCatalogPriceListsAction(companyId)),
        ])

        if (!listItem) {
          history.push(ROUTES.quickListSettings)
          return
        }

        setQuickList(listItem)
        setCatalog(catalogResult.catalogItem)
        setHasCatalogLoadError(catalogResult.failed)
        setPriceLists(lists || EMPTY_PRICE_LISTS)
        await formik.setValues(
          mapCatalogFormValues({
            quickList: listItem,
            catalog: catalogResult.catalogItem,
          })
        )
        if (catalogResult.failed) {
          showMessage(
            t('QUICK_LIST_CATALOG.MESSAGE.FAIL_LOAD_CATALOG'),
            'error'
          )
        }
      } catch (error) {
        showMessage(t('QUICK_LIST_CATALOG.MESSAGE.FAIL_LOAD'), 'error')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [id, companyId])

  // Tras la carga masiva se recarga el catálogo guardado y se aplican al formulario solo los
  // precios e imágenes que cambiaron, sin perder otros cambios sin guardar.
  const handleBulkUpdated = async () => {
    try {
      const updatedCatalog = await dispatch(getQuickListCatalogAction(id))
      if (!updatedCatalog) throw new Error('Catalog refresh returned no data')

      // Se fusiona sobre los productos actuales, no los del inicio de la carga, para conservar
      // lo editado mientras se procesaba el archivo.
      formik.setFieldValue(
        'products',
        mergeBulkUpdatedProducts(
          latestValuesRef.current.products,
          catalog,
          updatedCatalog
        )
      )
      setCatalog(updatedCatalog)
      setHasPendingCatalogSync(false)
      return true
    } catch (error) {
      setHasPendingCatalogSync(true)
      showMessage(t('QUICK_LIST_CATALOG.MESSAGE.FAIL_REFRESH'), 'error')
      return false
    }
  }

  // Reintenta leer el catálogo guardado cuando falló la carga inicial y lo aplica completo.
  const handleRetryCatalogLoad = async () => {
    try {
      const catalogItem = await dispatch(getQuickListCatalogAction(id))
      setCatalog(catalogItem)
      setHasCatalogLoadError(false)
      await formik.setValues(
        mapCatalogFormValues({ quickList, catalog: catalogItem })
      )
    } catch (error) {
      showMessage(t('QUICK_LIST_CATALOG.MESSAGE.FAIL_LOAD_CATALOG'), 'error')
    }
  }

  const handleSave = async () => {
    const errors = await formik.validateForm()
    const touched = GENERAL_FIELDS.reduce(
      (fields, field) => ({ ...fields, [field]: true }),
      { sections: true, products: true }
    )
    formik.setTouched(touched, false)

    if (Object.keys(errors).length > 0) {
      if (GENERAL_FIELDS.some(field => errors[field])) setStep(0)
      else if (errors.sections) setStep(1)
      else if (errors.products) setStep(2)
      showMessage(t('QUICK_LIST_CATALOG.VALIDATION.FORM_ERRORS'), 'warning')
      return
    }

    formik.handleSubmit()
  }

  if (isLoading) {
    return <MiniLoading full />
  }

  const stepContent = [
    <CatalogGeneralStep
      key="general"
      formik={formik}
      priceLists={priceLists}
      entityId={id}
    />,
    <CatalogSectionsStep
      key="sections"
      formik={formik}
      entityId={id}
      sectionsError={formik.touched.sections ? formik.errors.sections : ''}
      latestValuesRef={latestValuesRef}
    />,
    <CatalogProductsStep
      key="products"
      formik={formik}
      entityId={id}
      catalogId={catalog?.CatalogId}
      onBulkUpdated={handleBulkUpdated}
      onNotify={showMessage}
      latestValuesRef={latestValuesRef}
    />,
  ]

  return (
    <>
      <div className="quick-list-information quick-list-catalog">
        <div className="quick-list-information-card">
          <p className="catalog-list-name" data-testid="text-list-name-catalog">
            {t('QUICK_LIST_CATALOG.SUBTITLE', { name: quickList?.Name || '' })}
          </p>

          {!isQuickListActive && (
            <div
              className="catalog-alert"
              role="alert"
              data-testid="container-list-inactive-catalog">
              {t('QUICK_LIST_CATALOG.MESSAGE.LIST_INACTIVE')}
            </div>
          )}
          {catalog && isQuickListActive && !catalog.IsAvailable && (
            <div
              className="catalog-alert"
              role="alert"
              data-testid="container-unavailable-catalog">
              {t('QUICK_LIST_CATALOG.MESSAGE.UNAVAILABLE')}
            </div>
          )}
          {formik.values.products.length === 0 && (
            <div
              className="catalog-alert"
              role="alert"
              data-testid="container-list-empty-catalog">
              {t('QUICK_LIST_CATALOG.MESSAGE.LIST_EMPTY')}
            </div>
          )}
          {hasCatalogLoadError && (
            <div
              className="catalog-alert"
              role="alert"
              data-testid="container-load-error-catalog">
              <span>{t('QUICK_LIST_CATALOG.MESSAGE.FAIL_LOAD_CATALOG')}</span>{' '}
              <button
                type="button"
                className="catalog-link-button"
                onClick={handleRetryCatalogLoad}
                data-testid="btn-retry-load-catalog">
                {t('QUICK_LIST_CATALOG.ACTIONS.RETRY')}
              </button>
            </div>
          )}
          {hasPendingCatalogSync && (
            <div
              className="catalog-alert"
              role="alert"
              data-testid="container-sync-error-catalog">
              <span>{t('QUICK_LIST_CATALOG.MESSAGE.FAIL_REFRESH')}</span>{' '}
              <button
                type="button"
                className="catalog-link-button"
                onClick={handleBulkUpdated}
                data-testid="btn-retry-sync-catalog">
                {t('QUICK_LIST_CATALOG.ACTIONS.RETRY')}
              </button>
            </div>
          )}

          <nav
            className="catalog-stepper"
            aria-label={t('QUICK_LIST_CATALOG.TITLE')}>
            {CATALOG_STEPS.map((stepKey, index) => (
              <button
                key={stepKey}
                type="button"
                className={index === step ? 'is-active' : ''}
                aria-current={index === step ? 'step' : undefined}
                onClick={() => setStep(index)}
                data-testid={`btn-step-${stepKey.toLowerCase()}`}>
                <span className="catalog-stepper-number">{index + 1}</span>
                {t(`QUICK_LIST_CATALOG.STEPS.${stepKey}`)}
              </button>
            ))}
          </nav>

          {stepContent[step]}

          <div className="quick-list-information-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => history.push(ROUTES.quickListSettings)}
              disabled={isSubmitting}
              data-testid="btn-cancel-catalog">
              {t('QUICK_LIST_CATALOG.ACTIONS.CANCEL')}
            </button>
            {step > 0 && (
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setStep(step - 1)}
                disabled={isSubmitting}
                data-testid="btn-prev-step">
                {t('QUICK_LIST_CATALOG.ACTIONS.BACK')}
              </button>
            )}
            {step < CATALOG_STEPS.length - 1 && (
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setStep(step + 1)}
                disabled={isSubmitting}
                data-testid="btn-next-step">
                {t('QUICK_LIST_CATALOG.ACTIONS.NEXT')}
              </button>
            )}
            <button
              type="button"
              className={`btn-save${isSubmitting ? ' is-loading' : ''}`}
              disabled={
                isSubmitting ||
                hasPendingCatalogSync ||
                hasCatalogLoadError ||
                !isQuickListActive ||
                formik.values.products.length === 0
              }
              aria-busy={isSubmitting}
              onClick={handleSave}
              data-testid="btn-save-catalog">
              <span className="btn-label">
                {t('QUICK_LIST_CATALOG.ACTIONS.SAVE')}
              </span>
              {isSubmitting && (
                <CircularProgress
                  size="1.6rem"
                  color="inherit"
                  className="btn-spinner"
                />
              )}
            </button>
          </div>
        </div>
      </div>

      <CustomSnackbar
        open={snackbarMessage.open}
        message={snackbarMessage.message}
        severity={snackbarMessage.severity}
        testId="container-snackbar-catalog"
        onClose={() =>
          setSnackbarMessage({ open: false, message: '', severity: 'warning' })
        }
      />
    </>
  )
}

export default QuickListCatalog
